import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    List,
    ListItem,
    Paper,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import {
    deleteStaff,
    getTenantStaff,
    postStaff,
} from "../api/staff";
import { getUsers } from "../api/users";
import FeedbackSnackbar from "../components/FeedbackSnackbar";
import Loading from "../components/Loading";


const getOwnerUserLabel = (user) => {
  const displayName = user?.display_name?.trim();
  return displayName || "アカウント名未設定";
};

const displayNameMatchesQuery = (user, rawQuery) => {
  const q = rawQuery.trim().toLowerCase();
  if (!q) {
    return true;
  }
  return (user?.display_name || "").trim().toLowerCase().includes(q);
};


function EditStaff() {
  const [loading, setLoading] = useState(true);
  const [staffByOwnerUser, setStaffByOwnerUser] = useState({});
  const [ownerUsers, setOwnerUsers] = useState([]);
  const [selectedOwnerUserId, setSelectedOwnerUserId] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [newUserFamilyName, setNewUserFamilyName] = useState("");
  const [newUserGivenName, setNewUserGivenName] = useState("");
  const [feedback, setFeedback] = useState({ open: false, message: "", severity: "info", action: null });
  const deleteTimerRef = useRef(null);

  const fetchData = useCallback(async (currentSelectedOwnerUserId = "") => {
    const [staff, tenantUsers] = await Promise.all([getTenantStaff(), getUsers()]);
    const sortedStaff = staff.sort((a, b) =>
      a.family_name.localeCompare(b.family_name)
    );
    const sortedOwnerUsers = [...tenantUsers]
      .filter((u) => u.is_active)
      .sort((a, b) => getOwnerUserLabel(a).localeCompare(getOwnerUserLabel(b)));
    const groupedByOwnerUser = sortedStaff.reduce((acc, cur) => {
      const ownerUserId = cur.owner_user_id;
      if (!acc[ownerUserId]) {
        acc[ownerUserId] = [];
      }
      acc[ownerUserId].push(cur);
      return acc;
    }, {});
    const selectedOwnerExists = sortedOwnerUsers.some(
      (user) => user.id === currentSelectedOwnerUserId
    );
    setStaffByOwnerUser(groupedByOwnerUser);
    setOwnerUsers(sortedOwnerUsers);
    if (sortedOwnerUsers.length === 0) {
      setSelectedOwnerUserId("");
    } else if (currentSelectedOwnerUserId && !selectedOwnerExists) {
      setSelectedOwnerUserId("");
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOwnerUserChange = (_event, newValue) => {
    setSelectedOwnerUserId(newValue?.id ?? "");
    setSelectedUsers([]);
  };

  if (loading) {
    return <Loading />;
  }

  const handleUserChange = (event) => {
    const userId = event.target.value;
    if (event.target.checked) {
      setSelectedUsers([...selectedUsers, userId]);
    } else {
      setSelectedUsers(selectedUsers.filter((id) => id !== userId));
    }
  };

  const handleDelete = () => {
    const toDelete = [...selectedUsers];
    const deletedNames = toDelete
      .map((id) => {
        const user = (staffByOwnerUser[selectedOwnerUserId] || []).find((u) => u.id === id);
        return user ? `${user.family_name} ${user.given_name}` : id;
      })
      .join(", ");

    setStaffByOwnerUser((prev) => ({
      ...prev,
      [selectedOwnerUserId]: (prev[selectedOwnerUserId] || []).filter(
        (u) => !toDelete.includes(u.id)
      ),
    }));
    setSelectedUsers([]);

    if (deleteTimerRef.current) clearTimeout(deleteTimerRef.current);

    const undoAction = (
      <Button
        color="inherit"
        size="small"
        onClick={() => {
          clearTimeout(deleteTimerRef.current);
          deleteTimerRef.current = null;
          fetchData(selectedOwnerUserId);
          setFeedback((prev) => ({ ...prev, open: false }));
        }}
      >
        取消
      </Button>
    );

    setFeedback({
      open: true,
      message: `${deletedNames} を削除しました`,
      severity: "success",
      action: undoAction,
    });

    deleteTimerRef.current = setTimeout(async () => {
      await Promise.all(
        toDelete.map((userId) => {
          const targetUser = (staffByOwnerUser[selectedOwnerUserId] || []).find(
            (u) => u.id === userId
          );
          return targetUser ? deleteStaff(targetUser.public_id) : Promise.resolve();
        })
      );
      fetchData(selectedOwnerUserId);
      deleteTimerRef.current = null;
    }, 5000);
  };

  const handleAddUser = async () => {
    if (newUserFamilyName && newUserGivenName) {
      if (!selectedOwnerUserId) {
        return;
      }
      const newUser = {
        family_name: newUserFamilyName,
        given_name: newUserGivenName,
        owner_user_id: selectedOwnerUserId,
      };
      await postStaff(newUser);
      setNewUserFamilyName("");
      setNewUserGivenName("");
      // Re-fetch data to update the user list after addition
      fetchData(selectedOwnerUserId);
    }
  };

  const usersInCurrentOwner = staffByOwnerUser[selectedOwnerUserId] || [];
  const selectedOwner =
    ownerUsers.find((user) => user.id === selectedOwnerUserId) || null;

  return (
    <Stack spacing={2}>
      <Paper sx={{ p: 2.5 }}>
        <Stack spacing={1.5}>
          <Typography variant="subtitle1">アカウント</Typography>
          <Autocomplete
            fullWidth
            options={ownerUsers}
            value={selectedOwner}
            onChange={handleOwnerUserChange}
            getOptionLabel={(option) => getOwnerUserLabel(option)}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            filterOptions={(options, state) =>
              options.filter((u) => displayNameMatchesQuery(u, state.inputValue))
            }
            disabled={ownerUsers.length === 0}
            noOptionsText="該当するアカウントがありません"
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={!selectedOwner ? "アカウントを選択" : ""}
              />
            )}
          />
          {ownerUsers.length === 0 && (
            <Alert severity="warning">利用可能なアカウントがありません。</Alert>
          )}
        </Stack>
      </Paper>

      <Paper sx={{ p: 2.5 }}>
        <Stack spacing={1.5}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="subtitle1">登録済みクルー</Typography>
            <Button
              variant="contained"
              color="error"
              size="small"
              onClick={handleDelete}
              disabled={selectedUsers.length === 0}
            >
              選択を削除
            </Button>
          </Box>
          {!selectedOwnerUserId ? (
            <Alert severity="info">アカウントを選択してください。</Alert>
          ) : usersInCurrentOwner.length === 0 ? (
            <Alert severity="info">このアカウントにはクルーが登録されていません。</Alert>
          ) : (
            <List disablePadding dense>
              {usersInCurrentOwner.map((user, index) => (
                <ListItem key={user.id} disablePadding divider={index < usersInCurrentOwner.length - 1}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        value={user.id}
                        checked={selectedUsers.includes(user.id)}
                        onChange={handleUserChange}
                      />
                    }
                    label={`${user.family_name} ${user.given_name}`}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Stack>
      </Paper>

      <Paper sx={{ p: 2.5 }}>
        <Stack spacing={1.5}>
          <Typography variant="subtitle1">クルーを追加</Typography>
          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { sm: "flex-start" },
            }}
          >
            <TextField
              label="姓"
              value={newUserFamilyName}
              onChange={(e) => setNewUserFamilyName(e.target.value)}
            />
            <TextField
              label="名"
              value={newUserGivenName}
              onChange={(e) => setNewUserGivenName(e.target.value)}
            />
            <Button
              variant="contained"
              onClick={handleAddUser}
              disabled={!selectedOwnerUserId}
              sx={{ minWidth: "auto", whiteSpace: "nowrap", minHeight: 40 }}
            >
              追加
            </Button>
          </Box>
        </Stack>
      </Paper>
      <FeedbackSnackbar
        open={feedback.open}
        message={feedback.message}
        severity={feedback.severity}
        action={feedback.action}
        onClose={() => setFeedback((prev) => ({ ...prev, open: false }))}
      />
    </Stack>
  );
}

export default EditStaff;
